import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import Datasheet from 'react-datasheet';
import range from 'lodash/range';

import { User, userFactory } from '@/types/user';
import { filterEmptyUser } from '@/components/pages/Subscription/Presentation/Form/Field/Users/utils';
import { buildUpdateUsers } from '@/components/pages/Subscription/Presentation/Form/Field/Users/datasheetUtils';
import { Selection } from '@/components/pages/Subscription/Presentation/Form/Field/Users/UsersDatasheet';
import { NONGLSView } from '@/components/pages/Subscription/Presentation/Form/Field/Users/NONGLS/NONGLSView';

const INITIAL_ROW_SIZE = 10;
const COLUMN_SIZE = 5;

type OnCellsChanged = (
  changes: Datasheet.CellsChangedArgs<any, any>,
  additions: Datasheet.CellAdditionsArgs<any> | undefined,
) => void;

type Props = {
  value: User[];
  serviceGroupId?: number | null;
  errors?: any;
  onChange: (users: User[]) => void;
  children: ReactNode;
};

export const NONGLSContainer: React.FC<Props> = ({
  value,
  serviceGroupId,
  errors,
  onChange,
  children,
}) => {
  const [users, setUsers] = useState<User[]>([
    ...value,
    ...range(
      INITIAL_ROW_SIZE < value.length ? 0 : INITIAL_ROW_SIZE - value.length,
    ).map(() => userFactory()),
  ]);
  const [cellSelection, setCellSelection] = useState<Selection | null>(null);
  const [isPasting, setIsPasting] = useState(false);
  const datasheetRef = useRef<HTMLElement>(null);

  const onCellsChanged = useCallback<OnCellsChanged>(
    (changes, additions = []) => {
      if (changes.length >= COLUMN_SIZE) {
        setIsPasting(true);
      }

      const newUsers = buildUpdateUsers(
        users,
        [...changes, ...additions],
        COLUMN_SIZE,
      );

      // update local state
      setUsers(newUsers);

      setTimeout(() => {
        // Pass data to parent component
        onChange(filterEmptyUser(newUsers));
      }, 0);

      setTimeout(() => {
        setIsPasting(false);
      }, 16);
    },
    [onChange, users],
  );
  const pageClick = useCallback(
    (e: Event) => {
      // データシートの外部をクリックした場合にセルの選択状態を解除する
      if (!datasheetRef?.current?.contains(e.target as Node)) {
        setCellSelection(null);
      }
    },
    [datasheetRef],
  );

  useEffect(() => {
    document.addEventListener('mousedown', pageClick);
    return () => {
      document.removeEventListener('mousedown', pageClick);
    };
  }, [pageClick]);

  const childProps = {
    errors,
    serviceGroupId,
    users,
    setUsers,
    cellSelection,
    setCellSelection,
    onCellsChanged,
    isPasting,
    datasheetRef,
    children,
  };
  return <NONGLSView {...childProps} />;
};
